import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import ExperienceCard from '../components/ExperienceCard';
import EducationCard from '../components/EducationCard';
import DynamicList from '../components/dynamic/DynamicList';
import { experienceAPI, educationAPI } from '../services/api';
import { Experience, Education } from '../types';
import { motion } from 'framer-motion';
import { getExperienceYears } from '../utils/experience';

const About: React.FC = () => {
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [education, setEducation] = useState<Education[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const experienceYears = getExperienceYears('2021-02');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [expRes, eduRes] = await Promise.all([
          experienceAPI.getAll(),
          educationAPI.getAll(),
        ]);
        setExperiences(expRes.data);
        setEducation(eduRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div className="pt-20">
      <DynamicSection
        id="about"
        title="About Me"
        subtitle="Passionate .NET Developer with expertise in modern technologies"
        className="bg-white dark:bg-gray-900"
      >
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="mb-12 flex flex-col md:flex-row items-center gap-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex-shrink-0">
              <img
                src="/images/adarsh-chaunal-profile-main.jpg"
                alt="Adarsh Chaunal"
                className="w-64 h-64 rounded-lg object-cover shadow-2xl border-4 border-primary-200 dark:border-primary-800"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                I'm a <strong>Results-driven Software Developer</strong> with <strong>{experienceYears} years of experience</strong> in designing, developing, and deploying full-stack web applications using <strong>.NET</strong>, <strong>React</strong>, and <strong>SQL Server</strong>. Skilled in applying <strong>Clean Architecture</strong> and <strong>SOLID principles</strong> to deliver secure, scalable, and high-performing solutions.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            className="prose prose-lg max-w-none mb-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              I specialize in building applications using <strong>Blazor WebAssembly</strong>, <strong>Blazor Server</strong>, and <strong>React.js with TypeScript</strong>. My expertise includes implementing authentication flows with <strong>OAuth 2.0</strong>, <strong>OIDC</strong>, and <strong>Duende Identity Server</strong>, integrating <strong>Azure services</strong> (Functions, Graph API, Computer Vision, DevOps), and working with <strong>AI/ML technologies</strong> including Machine Learning, Deep Learning, Computer Vision, and Generative AI.
            </p>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              I'm adept at collaborating with cross-functional teams, integrating third-party APIs, and leveraging modern tools to build innovative, business-driven software solutions. Currently serving as <strong>Assistant Team Lead</strong> at LnP Infotech, I continue to contribute technical expertise and problem-solving skills to deliver end-to-end development solutions.
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">Loading...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
                <h3 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">Professional Experience</h3>
                <DynamicList
                  items={experiences}
                  renderItem={(exp, index) => (
                    <ExperienceCard experience={exp} index={index} />
                  )}
                  className="space-y-6"
                  emptyMessage="No experience data available."
                />
            </div>

            <div>
                <h3 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">Education</h3>
                <DynamicList
                  items={education}
                  renderItem={(edu, index) => (
                    <EducationCard education={edu} index={index} />
                  )}
                  className="space-y-6"
                  emptyMessage="No education data available."
                />
              </div>
            </div>
          )}
        </div>
      </DynamicSection>
    </div>
  );
};

export default About;
